// pipelineState — the pure projection behind usePipelineState. Kept
// dependency-free so it can be tested without the app's module graph.
//
// Web mirror of iOS `NewIAPipeline` (Kit/NewIAPipeline.swift): "unpriced",
// "not matched yet", "nothing imported" are moments in a job while an import
// or a calculation runs, not verdicts. The backend state machines are
// authoritative (docs/architecture/sync-tax-state.md — moonscape.sync.state
// and moonscape.operation_status); this only projects them.

export type PipelineState = "quiet" | "importing" | "calculating";

const ACTIVE_SYNC_STATES = new Set([
  "queued",
  "starting",
  "validating_api_keys",
  "in_progress",
]);

export const derivePipeline = (
  syncs: Array<{ state?: string; status?: string }> | undefined,
  taxStatus: { status?: string } | undefined
): PipelineState => {
  const importing = (syncs ?? []).some(
    (s) => ACTIVE_SYNC_STATES.has(s.state ?? "") || s.status === "processing"
  );
  if (importing) return "importing";
  if (taxStatus?.status === "running" || taxStatus?.status === "pending")
    return "calculating";
  return "quiet";
};

/** Has the engine ever produced a figure for this user? Read from
 * `operation_status`, never inferred from row counts (I1). A price prompt
 * before this is true asks the user to buy a number they have not seen. */
export const hasCompletedCalc = (
  taxStatus: { status?: string } | undefined
): boolean => taxStatus?.status === "completed";

/** One-line label a row shows in place of a "nothing here" verdict. */
export const pipelineRowLabel = (pipeline: PipelineState): string | null =>
  pipeline === "importing"
    ? "Import in progress"
    : pipeline === "calculating"
      ? "Calculating now"
      : null;
